import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Card, CardHeader, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  FileText, 
  Eye,
  Download,
  Sparkles,
  Plus,
  Search,
  Package,
  UserCheck
} from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import AIFormCreator from '@/components/forms/AIFormCreator';
import FormRendererBranded from '@/components/forms/FormRendererBranded';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

const FormsPageKanban = () => {
  const [activeView, setActiveView] = useState('templates'); // templates, vendor, employee, create
  const [templates, setTemplates] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Kanban columns for submissions
  const [columns, setColumns] = useState({
    draft: [],
    active: [],
    completed: [],
    archived: []
  });
  
  // Preview states
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);

  useEffect(() => {
    fetchTemplates();
    fetchSubmissions();
  }, []);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/forms/templates');
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Failed to fetch templates:', error);
      toast.error('Failed to load form templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSubmissions = async () => {
    try {
      const response = await api.get('/forms/all-responses');
      const submissions = response.data || [];
      
      // Organize into Kanban columns
      const newColumns = {
        draft: submissions.filter(s => s.status === 'draft'),
        active: submissions.filter(s => s.status === 'submitted'),
        completed: submissions.filter(s => s.status === 'approved'),
        archived: submissions.filter(s => s.status === 'rejected')
      };
      
      setColumns(newColumns);
      setSubmissions(submissions);
    } catch (error) {
      console.error('Failed to fetch submissions:', error);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const { source, destination, draggableId } = result;
    
    if (source.droppableId === destination.droppableId) return;

    // Update backend status
    const statusMap = {
      draft: 'draft',
      active: 'submitted',
      completed: 'approved',
      archived: 'rejected'
    };

    try {
      await api.put(`/forms/response/${draggableId}/status?status=${statusMap[destination.droppableId]}`);
      
      // Update local state
      const newColumns = { ...columns };
      const sourceItems = Array.from(newColumns[source.droppableId]);
      const destItems = Array.from(newColumns[destination.droppableId]);
      const [movedItem] = sourceItems.splice(source.index, 1);
      destItems.splice(destination.index, 0, movedItem);
      
      newColumns[source.droppableId] = sourceItems;
      newColumns[destination.droppableId] = destItems;
      
      setColumns(newColumns);
      toast.success('Form status updated');
    } catch (error) {
      toast.error('Failed to update form status');
    }
  };

  const handleDownloadPDF = async (responseId) => {
    try {
      const response = await api.get(`/forms/response/${responseId}/pdf`, {
        responseType: 'blob'
      });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `form_${responseId.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF downloaded successfully');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const getCategoryBadge = (category) => {
    const colors = {
      hr: 'bg-[#C9A961]/20 text-[#C9A961] border-[#C9A961]/50',
      safety: 'bg-[#D4C278]/20 text-[#D4C278] border-[#D4C278]/50',
      project: 'bg-[#C9A961]/30 text-[#FFD700] border-[#FFD700]/50',
      client: 'bg-[#B8860B]/20 text-[#B8860B] border-[#B8860B]/50',
      quality: 'bg-[#DAA520]/20 text-[#DAA520] border-[#DAA520]/50',
      general: 'bg-gray-500/20 text-gray-300 border-gray-500/50'
    };
    return (
      <Badge className={`${colors[category] || colors.general} border`}>
        {category?.toUpperCase() || 'GENERAL'}
      </Badge>
    );
  };

  const filteredTemplates = templates.filter(template => 
    template.template_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    template.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const vendorForms = templates.filter(t => 
    ['Vendor Service Agreement', 'Conditional Lien Waiver and Release'].includes(t.template_name)
  );
  
  const employeeForms = templates.filter(t => 
    ['Employee Timesheet', 'Independent Contractor Agreement', 'Non-Disclosure Agreement (NDA)', 'Non-Compete Agreement for Contractors'].includes(t.template_name)
  );

  const KanbanColumn = ({ title, items, columnId, color }) => (
    <div className="flex-1 min-w-[300px]">
      <div className={`bg-gradient-to-r ${color} border-2 border-[#C9A961] rounded-t-xl p-4`}>
        <h3 className="text-xl font-bold text-[#C9A961] flex items-center justify-between">
          {title}
          <Badge className="bg-black/40 text-[#C9A961] border-[#C9A961]/50">
            {items.length}
          </Badge>
        </h3>
      </div>
      <Droppable droppableId={columnId}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`min-h-[500px] bg-black/40 border-2 border-t-0 border-[#C9A961]/30 rounded-b-xl p-4 space-y-3 ${
              snapshot.isDraggingOver ? 'bg-[#C9A961]/10' : ''
            }`}
          >
            {items.map((item, index) => (
              <Draggable key={item.response_id} draggableId={item.response_id} index={index}>
                {(provided, snapshot) => (
                  <Card
                    ref={provided.innerRef}
                    {...provided.draggableProps}
                    {...provided.dragHandleProps}
                    className={`bg-black border-[#C9A961]/30 hover:border-[#C9A961] transition-all ${
                      snapshot.isDragging ? 'shadow-2xl shadow-[#C9A961]/50 scale-105' : ''
                    }`}
                  >
                    <CardHeader className="pb-3">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="text-white font-semibold text-sm mb-1">
                            {item.template_name || 'Unknown Form'}
                          </h4>
                          <p className="text-gray-400 text-xs">
                            {item.filled_by_name || 'Unknown'}
                          </p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="flex items-center justify-between">
                        <span className="text-gray-500 text-xs">
                          {item.filled_at ? new Date(item.filled_at).toLocaleDateString() : 'N/A'}
                        </span>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-[#C9A961] hover:bg-[#C9A961]/20 h-7 w-7 p-0"
                            onClick={() => handleDownloadPDF(item.response_id)}
                          >
                            <Download className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </Draggable>
            ))}
            {provided.placeholder}
            {items.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No forms here</p>
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black p-8 space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-black via-[#C9A961]/20 to-black border-2 border-[#C9A961] rounded-xl p-8">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <img src="/williams-logo-transparent.png" alt="Williams Diversified" className="h-20" />
            <div>
              <h1 className="text-5xl font-bold mb-2 flex items-center gap-3 font-serif">
                <FileText className="h-12 w-12 text-[#C9A961]" />
                <span className="text-white">Forms</span>{' '}
                <span className="text-[#C9A961] italic">Management</span>
              </h1>
              <p className="text-gray-400 text-xl">
                Professional Document Management • Powered by AI
              </p>
            </div>
          </div>
          <Button
            onClick={() => setCreateDialogOpen(true)}
            className="bg-[#C9A961] text-black hover:bg-[#D4C278] h-14 px-8 text-lg"
          >
            <Sparkles className="h-5 w-5 mr-2" />
            AI Create Form
          </Button>
        </div>
      </div>

      {/* View Tabs */}
      <div className="flex gap-4 bg-black border-2 border-[#C9A961] rounded-xl p-2">
        <Button
          onClick={() => setActiveView('templates')}
          className={`flex-1 h-14 text-lg ${activeView === 'templates' ? 'bg-[#C9A961] text-black' : 'bg-transparent text-gray-400 hover:text-[#C9A961] hover:bg-[#C9A961]/10'}`}
        >
          <FileText className="h-5 w-5 mr-2" />
          Templates ({templates.length})
        </Button>
        <Button
          onClick={() => setActiveView('submissions')}
          className={`flex-1 h-14 text-lg ${activeView === 'submissions' ? 'bg-[#C9A961] text-black' : 'bg-transparent text-gray-400 hover:text-[#C9A961] hover:bg-[#C9A961]/10'}`}
        >
          <FileText className="h-5 w-5 mr-2" />
          Submissions ({submissions.length})
        </Button>
        <Button
          onClick={() => setActiveView('vendor')}
          className={`flex-1 h-14 text-lg ${activeView === 'vendor' ? 'bg-[#C9A961] text-black' : 'bg-transparent text-gray-400 hover:text-[#C9A961] hover:bg-[#C9A961]/10'}`}
        >
          <Package className="h-5 w-5 mr-2" />
          Vendor Onboarding
        </Button>
        <Button
          onClick={() => setActiveView('employee')}
          className={`flex-1 h-14 text-lg ${activeView === 'employee' ? 'bg-[#C9A961] text-black' : 'bg-transparent text-gray-400 hover:text-[#C9A961] hover:bg-[#C9A961]/10'}`}
        >
          <UserCheck className="h-5 w-5 mr-2" />
          Employee Onboarding
        </Button>
      </div>

      {/* Templates View */}
      {activeView === 'templates' && (
        <div className="space-y-6">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-[#C9A961]" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-12 bg-black border-[#C9A961] text-white h-14 text-lg"
            />
          </div>

          {/* Templates Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {filteredTemplates.map((template) => (
              <Card key={template.template_id} className="bg-black/60 border-[#C9A961]/30 hover:border-[#C9A961] transition-all hover:scale-105">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h3 className="text-white font-bold text-lg mb-2">{template.template_name}</h3>
                      {getCategoryBadge(template.category)}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {template.description || 'No description available'}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-500 text-sm">
                      {template.fields?.length || 0} fields
                    </span>
                    <Button
                      size="sm"
                      onClick={() => {
                        setSelectedTemplate(template);
                        setPreviewDialogOpen(true);
                      }}
                      className="bg-[#C9A961] text-black hover:bg-[#D4C278]"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Preview
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Submissions Kanban View */}
      {activeView === 'submissions' && (
        <DragDropContext onDragEnd={handleDragEnd}>
          <div className="flex gap-6 overflow-x-auto pb-4">
            <KanbanColumn
              title="Draft"
              items={columns.draft}
              columnId="draft"
              color="from-black via-gray-800 to-black"
            />
            <KanbanColumn
              title="Active"
              items={columns.active}
              columnId="active"
              color="from-black via-[#C9A961]/10 to-black"
            />
            <KanbanColumn
              title="Completed"
              items={columns.completed}
              columnId="completed"
              color="from-black via-[#D4C278]/10 to-black"
            />
            <KanbanColumn
              title="Archived"
              items={columns.archived}
              columnId="archived"
              color="from-black via-gray-700 to-black"
            />
          </div>
        </DragDropContext>
      )}

      {/* Vendor Onboarding View */}
      {activeView === 'vendor' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-[#C9A961]/10 to-transparent border-l-4 border-[#C9A961] p-6">
            <h2 className="text-2xl font-bold text-[#C9A961] mb-2 flex items-center gap-2">
              <Package className="h-6 w-6" />
              Vendor Onboarding Packet
            </h2>
            <p className="text-gray-300">Complete bundle of forms required for vendor onboarding</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {vendorForms.map((template) => (
              <Card key={template.template_id} className="bg-black/60 border-[#C9A961]/30 hover:border-[#C9A961] transition-all">
                <CardHeader>
                  <h3 className="text-white font-bold text-lg">{template.template_name}</h3>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm mb-4">{template.description}</p>
                  <Button
                    onClick={() => {
                      setSelectedTemplate(template);
                      setPreviewDialogOpen(true);
                    }}
                    className="w-full bg-[#C9A961] text-black hover:bg-[#D4C278]"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View & Fill
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Employee Onboarding View */}
      {activeView === 'employee' && (
        <div className="space-y-6">
          <div className="bg-gradient-to-r from-[#C9A961]/10 to-transparent border-l-4 border-[#C9A961] p-6">
            <h2 className="text-2xl font-bold text-[#C9A961] mb-2 flex items-center gap-2">
              <UserCheck className="h-6 w-6" />
              Employee Onboarding Packet
            </h2>
            <p className="text-gray-300">Complete bundle of forms required for employee onboarding</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {employeeForms.map((template) => (
              <Card key={template.template_id} className="bg-black/60 border-[#C9A961]/30 hover:border-[#C9A961] transition-all">
                <CardHeader>
                  <h3 className="text-white font-bold text-lg">{template.template_name}</h3>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-400 text-sm mb-4">{template.description}</p>
                  <Button
                    onClick={() => {
                      setSelectedTemplate(template);
                      setPreviewDialogOpen(true);
                    }}
                    className="w-full bg-[#C9A961] text-black hover:bg-[#D4C278]"
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    View & Fill
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Template Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-5xl bg-dark-card border-[#C9A961]/30 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#C9A961]">
              {selectedTemplate?.template_name}
            </DialogTitle>
          </DialogHeader>
          {selectedTemplate && (
            <FormRendererBranded
              template={selectedTemplate}
              onSubmit={() => {}}
              readOnly={true}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* AI Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-5xl bg-dark-card border-[#C9A961]/30 h-[80vh]">
          <DialogHeader>
            <DialogTitle className="text-2xl text-[#C9A961]">
              <Sparkles className="h-6 w-6 inline mr-2" />
              AI Form Creator
            </DialogTitle>
          </DialogHeader>
          <div className="h-full overflow-hidden">
            <AIFormCreator 
              onFormCreated={() => {
                fetchTemplates();
                setCreateDialogOpen(false);
              }} 
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FormsPageKanban;